import {
  Component,
  EventEmitter,
  Input,
  Output,
  signal,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { IconComponent } from '../icon';

@Component({
  selector: 'aprova-file-upload',
  standalone: true,
  imports: [CommonModule, IconComponent],
  templateUrl: './file-upload.component.html',
  styleUrl: './file-upload.component.scss',
})
export class FileUploadComponent {
  @Input() acceptedFormats: string[] = [
    'application/pdf',
    'image/jpeg',
    'image/png',
  ];
  @Input() maxSizeBytes: number = 5 * 1024 * 1024; // 5MB
  @Input() label: string = 'Arraste o arquivo aqui ou clique para selecionar';
  @Input() hint: string = 'PDF, JPG ou PNG — máximo 5MB';
  @Input() disabled: boolean = false;

  @Output() fileSelected = new EventEmitter<File>();
  @Output() fileRemoved = new EventEmitter<void>();

  public isDragging = signal(false);
  public selectedFile = signal<File | null>(null);
  public errorMessage = signal<string | null>(null);

  get acceptAttribute(): string {
    const map: Record<string, string> = {
      'application/pdf': '.pdf',
      'image/jpeg': '.jpg,.jpeg',
      'image/png': '.png',
    };
    return this.acceptedFormats
      .map((f) => map[f] ?? f)
      .join(',');
  }

  private get formatNames(): string {
    const map: Record<string, string> = {
      'application/pdf': 'PDF',
      'image/jpeg': 'JPG',
      'image/png': 'PNG',
    };
    return this.acceptedFormats.map((f) => map[f] ?? f).join(', ');
  }

  private get maxSizeLabel(): string {
    const mb = this.maxSizeBytes / (1024 * 1024);
    return mb >= 1 ? `${mb}MB` : `${this.maxSizeBytes / 1024}KB`;
  }

  get fileSize(): string {
    const file = this.selectedFile();
    if (!file) return '';
    const kb = file.size / 1024;
    if (kb < 1024) return `${kb.toFixed(0)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.disabled) {
      this.isDragging.set(true);
    }
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
    if (this.disabled) return;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.processFile(files[0]);
    }
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.processFile(input.files[0]);
    }
    input.value = '';
  }

  removeFile(): void {
    this.selectedFile.set(null);
    this.errorMessage.set(null);
    this.fileRemoved.emit();
  }

  private processFile(file: File): void {
    this.errorMessage.set(null);

    if (!this.acceptedFormats.includes(file.type)) {
      this.errorMessage.set(`Formato não suportado. Use ${this.formatNames}.`);
      return;
    }

    if (file.size > this.maxSizeBytes) {
      this.errorMessage.set(`Arquivo excede o tamanho máximo de ${this.maxSizeLabel}.`);
      return;
    }

    this.selectedFile.set(file);
    this.fileSelected.emit(file);
  }
}
